// Grid'e 2D blok yapıştırma — saf mantık (TODO-KPI-05, 2026-10-07). Panodaki Excel seçimini
// (TSV; `;`/`,` CSV yedek) hücre matrisine çözer, aktif hücreden başlayarak sağa/aşağı eşler ve
// bir yazma PLANI üretir: hangi hücreye ne yazılacak (+ geri alma için eski değer), hangileri
// neden atlanacak. Yan etki yok; bileşen (KpiGrid) planı önizletir, onayda sırayla kaydeder.
//
// Eşleme kuralları:
//   · bant (kategori) ve kırılım grup bandı satırları hücre taşımaz → pano satırı TÜKETMEDEN
//     atlanır (DR12; portal K3'te kategoriye taşınmış grup bandı da);
//   · hedef blokta bant atlandıysa ve panoda tamamen boş satır varsa uyarı (başlık satırı
//     kopyalanmış olabilir — eşleşme kayar); `skipEmptySourceRows` boş satırı tüketmeden geçer;
//   · hesaplanan (ƒx) satır, konsolide/ara toplam kolonu, portalda salt okunur satır/hücre →
//     pano hücresi tüketilir ama yazılmaz (sebep listede);
//   · tablo dışına taşan satır/kolon → "tablo dışı";
//   · boş pano hücresi mevcut değeri SİLMEZ (atlanır) — silmek için "-" yapıştırın (kural 1);
//   · değer `normalizeCellInput` (parseCell) kuralıyla: "XX" → XXX, "-" → boş, "27.405,87" TR;
//   · hücredeki değerle aynıysa yazılmaz ("değişmedi").
// Geri alma: `undoWrites(plan)` yazılanların ESKİ girdilerini ters sırada döndürür.

import type { KpiResolvedCell } from "@mahir/shared";
import { parseCell } from "@mahir/shared";
import {
  isEditable,
  normalizeCellInput,
  readCell,
  type CellInput,
  type CellPos,
  type GridColumn,
  type GridRow,
} from "./kpi-grid-nav";

export type PasteSkipReason =
  | "derived"
  | "aggregate"
  | "readonly"
  | "outside"
  | "empty"
  | "unchanged";

export interface PasteWrite {
  pos: CellPos;
  metricId: string;
  entityId: string;
  year: number;
  label: string;
  columnLabel: string;
  raw: string;
  input: CellInput;
  /** geri alma için hücrenin yapıştırma öncesi girdisi */
  prev: CellInput;
}

export interface PasteSkip {
  label: string;
  columnLabel: string;
  raw: string;
  reason: PasteSkipReason;
}

/** DR12 — önizlemede amber uyarı; `sourceRows` panodaki 1 tabanlı satır numaraları. */
export interface PasteWarning {
  kind: "empty_source_row";
  sourceRows: number[];
}

export interface PastePlan {
  writes: PasteWrite[];
  skipped: PasteSkip[];
  warnings: PasteWarning[];
  /** panodan çözülen matris boyutu */
  rows: number;
  cols: number;
}

export const PASTE_COPY = {
  title: "Panodan yapıştır",
  summary: (n: number, m: number): string =>
    `${n} hücre yazılacak${m > 0 ? `, ${m} atlanacak` : ""}`,
  nothing: "Yazılacak hücre yok — panodaki blok bu konuma uymuyor.",
  writesHeading: "Yazılacak",
  skippedHeading: "Atlanacak",
  more: (n: number): string => `+${n} daha`,
  confirm: (n: number): string => `Yapıştır (${n})`,
  cancel: "Vazgeç",
  done: (n: number): string => `${n} hücre yapıştırıldı`,
  partial: (ok: number, failed: number): string =>
    `${ok} hücre yapıştırıldı · ${failed} kaydedilemedi`,
  undo: "Geri al",
  undone: "Yapıştırma geri alındı",
  reasons: {
    derived: "hesaplanan gösterge",
    aggregate: "konsolide / ara toplam",
    readonly: "yalnız görüntüleme",
    outside: "tablo dışı",
    empty: "boş hücre",
    unchanged: "değişmedi",
  } satisfies Record<PasteSkipReason, string>,
  hint: "Excel'de bloğu kopyalayın, burada başlangıç hücresini seçip Ctrl/⌘+V ile yapıştırın.",
  /** önizleme liste sütun başlıkları — hedef satır etiketi her zaman gösterilir (DR12) */
  columns: { target: "Hedef satır", column: "Sütun", value: "Değer" },
  emptySourceRow: (rows: readonly number[]) =>
    `Panoda boş satır var (${rows.join(", ")}. satır) — kaynakta başlık satırı olabilir, eşleşmeyi kontrol edin.`,
  skipEmptyRows: "Boş satırı atla",
} as const;

// ─── Pano çözümleme ──────────────────────────────────────────────────────────

/** Ayraç: sekme > noktalı virgül > virgül (yalnız TR ondalık sanılamayacaksa) > tek kolon. */
export function detectDelimiter(text: string): "\t" | ";" | "," | null {
  if (text.includes("\t")) return "\t";
  if (text.includes(";")) return ";";
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) return null;
  for (const line of lines) {
    const commas = (line.match(/,/g) ?? []).length;
    if (commas >= 2) return ",";
    if (commas === 1 && parseCell(line).kind === "text") return ",";
  }
  return null;
}

/**
 * Excel panosu: satırlar `\r?\n`, hücreler sekme; `"`-tırnaklı hücre satır sonu/ayraç taşıyabilir
 * (`""` kaçış). Sondaki boş satır düşer; satırlar en geniş satıra göre boşla tamamlanır.
 */
export function parseClipboardGrid(text: string): string[][] {
  const delim = detectDelimiter(text);
  const out: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  let i = 0;
  const src = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  while (i < src.length) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }
    if (ch === '"' && cell === "") {
      quoted = true;
      i += 1;
      continue;
    }
    if (delim !== null && ch === delim) {
      row.push(cell);
      cell = "";
      i += 1;
      continue;
    }
    if (ch === "\n") {
      row.push(cell);
      out.push(row);
      row = [];
      cell = "";
      i += 1;
      continue;
    }
    cell += ch;
    i += 1;
  }
  if (cell !== "" || row.length > 0) {
    row.push(cell);
    out.push(row);
  }
  while (out.length > 0 && out[out.length - 1]!.every((c) => c.trim() === "")) out.pop();
  const width = out.reduce((w, r) => Math.max(w, r.length), 0);
  return out.map((r) => {
    const padded = r.map((c) => c.trim());
    while (padded.length < width) padded.push("");
    return padded;
  });
}

// ─── Plan ─────────────────────────────────────────────────────────────────────

/** Hücrenin mevcut durumunu kaydetme girdisine çevirir (geri alma / "değişmedi" kıyası). */
export function cellToInput(cell: KpiResolvedCell | undefined): CellInput {
  if (!cell) return { status: "empty" };
  switch (cell.status) {
    case "filled":
      return typeof cell.value === "number"
        ? { status: "filled", numericValue: cell.value }
        : cell.text
          ? { status: "filled", textValue: cell.text }
          : { status: "empty" };
    case "pending_xxx":
      return { status: "pending_xxx" };
    case "not_measured":
      return cell.text
        ? { status: "not_measured", textValue: cell.text }
        : { status: "not_measured" };
    default:
      return { status: "empty" };
  }
}

export function sameInput(a: CellInput, b: CellInput): boolean {
  return (
    a.status === b.status &&
    (a.numericValue ?? null) === (b.numericValue ?? null) &&
    (a.textValue ?? "") === (b.textValue ?? "")
  );
}

export interface PlanPasteArgs {
  rows: GridRow[];
  columns: GridColumn[];
  focus: CellPos;
  text: string;
  /** portal (müşteri) görünümü: `effectiveClientAccess !== "open"` satır ve `editable:false` hücre yazılamaz */
  clientView?: boolean | undefined;
  /** DR12 "Boş satırı atla": tamamen boş pano satırı hedef satır tüketmez */
  skipEmptySourceRows?: boolean | undefined;
}

export function planPaste(args: PlanPasteArgs): PastePlan {
  const { rows, columns, focus, text, clientView } = args;
  const matrix = parseClipboardGrid(text);
  const plan: PastePlan = {
    writes: [],
    skipped: [],
    warnings: [],
    rows: matrix.length,
    cols: matrix[0]?.length ?? 0,
  };
  // bant ve grup bandı satırlarını tüketmeden ilerle
  let r = focus.row;
  let bandSkipped = false;
  const emptyLines: number[] = [];
  const nextData = (from: number): number => {
    let i = from;
    while (i < rows.length && rows[i]!.isBand) {
      i += 1;
      bandSkipped = true;
    }
    return i;
  };
  for (const [li, line] of matrix.entries()) {
    const blank = line.every((c) => c === "");
    if (blank) {
      emptyLines.push(li + 1);
      if (args.skipEmptySourceRows) continue;
    }
    r = nextData(r);
    const row = rows[r];
    for (let j = 0; j < line.length; j += 1) {
      const raw = line[j]!;
      const col = columns[focus.col + j];
      // üye satırında tam yol ("Toplam › Cinsiyete göre › Kadın") — önizleme kardeşleri ayırır
      const label = row ? (row.member?.path ?? row.indicator.label) : "—";
      const columnLabel = col?.label ?? "—";
      const skip = (reason: PasteSkipReason): void => {
        plan.skipped.push({ label, columnLabel, raw, reason });
      };
      if (raw === "") {
        skip("empty"); // boş pano hücresi mevcut değeri silmez (dolgu hücreleri dahil)
        continue;
      }
      if (!row || !col) {
        skip("outside");
        continue;
      }
      if (row.indicator.isDerived) {
        skip("derived");
        continue;
      }
      if (col.aggregate || !col.entityId) {
        skip("aggregate");
        continue;
      }
      const cell = readCell(row.indicator, col);
      if (
        clientView &&
        (row.indicator.effectiveClientAccess !== "open" || cell?.editable === false)
      ) {
        skip("readonly");
        continue;
      }
      if (!isEditable(rows, columns, { row: r, col: focus.col + j })) {
        skip("readonly");
        continue;
      }
      const { input } = normalizeCellInput(raw, {
        unit: row.indicator.unit,
        valueType: row.indicator.valueType,
      });
      const prev = cellToInput(cell);
      if (sameInput(input, prev)) {
        skip("unchanged");
        continue;
      }
      plan.writes.push({
        pos: { row: r, col: focus.col + j },
        metricId: row.indicator.metricId,
        entityId: col.entityId,
        year: col.year,
        label,
        columnLabel,
        raw,
        input,
        prev,
      });
    }
    r += 1;
  }
  if (bandSkipped && emptyLines.length > 0 && !args.skipEmptySourceRows) {
    plan.warnings.push({ kind: "empty_source_row", sourceRows: emptyLines });
  }
  return plan;
}

/** Geri alma planı: yazılan hücrelere eski girdiler, ters sırada (son yazılan önce geri döner). */
export function undoWrites(writes: readonly PasteWrite[]): PasteWrite[] {
  return [...writes].reverse().map((w) => ({ ...w, input: w.prev, prev: w.input }));
}
